import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import KeyIcon from '@mui/icons-material/Key';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';

import { getMyChannels, resetStreamKey, StreamChannel } from '../../../services/channelsService';
import { deleteChannel, getTranscoders, Transcoder, updateChannel } from '../../../services/streamsService';
import { getChannelLogs } from '../../../services/auditService';
import {
  BillingAccountSummary,
  BillingServiceLine,
  getBillingAccountSummary,
  getResourceCancellations,
  deleteResourceNow,
  ResourceCancellation,
  scheduleResourceCancellation,
  undoResourceCancellation,
} from '../../../services/billingService';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { AddonStore } from '../AddonStore';
import { ResourceViewControls, ResourceViewMode } from './ResourceViewControls';
import { ConsolePageShell } from '../../console/ConsolePageShell';
import { formatAuditDetails } from '../../../utils/auditDetails';
import { formatDateDDMMYY, formatDateTimeDDMMYY } from '../../../utils/dateFormat';

type FilterKey = 'ALL' | 'ACTIVE' | 'INACTIVE' | 'ISSUES' | 'CAPACITY';
type DrawerTab = 'history' | 'settings';
type ConfirmAction = 'delete' | 'resetKey' | null;
type AuditLogEntry = { id: number; action: string; details: string; createdAt: string; userId: string };

const CANCELLABLE_SERVICE_TYPES = new Set([
  'DIRECT_OUTPUT',
  'SOCIAL_STREAM',
  'MANAGED_SERVICE',
  'MANAGED_SERVICE_UPGRADE',
  'MANAGED_OUTPUT',
  'MANAGED_UPGRADE',
  'HLS_STANDARD',
  'HLS_SCTE35',
  'HLS_PLUS',
  'EPG_XMLTV',
  'EPG_GRACENOTE',
  'PLAYKASTER',
  'OUTPUT_PACKAGE',
]);

interface CapacityBucket { label: string; owned: number; used: number; available: number; }
interface ChannelSummary {
  channel: StreamChannel;
  outputs: Transcoder[];
  activeOutputs: number;
  issues: number;
  direct: CapacityBucket;
  social: CapacityBucket;
  managedService: CapacityBucket;
  managed: CapacityBucket;
  hls: CapacityBucket;
  hlsPlus: CapacityBucket;
  totalOwned: number;
  totalUsed: number;
  totalAvailable: number;
}

const v = (name: string) => `var(${name})`;
const SKY = v('--primary-light');
const SKY_STRONG = v('--bg-card-hover');
const LIGHT = v('--bg-body');
const TEXT = v('--text-main');
const MUTED = v('--text-secondary');
const BORDER = v('--border');

const cardSx = { borderRadius: 0, border: '0.5px solid', borderColor: BORDER, bgcolor: v('--bg-card'), color: TEXT } as const;
const labelSx = { fontSize: '0.64rem', fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: MUTED } as const;
const monoSx = { fontFamily: "'IBM Plex Mono', 'DM Mono', monospace", fontSize: '0.76rem', fontWeight: 700, color: TEXT } as const;
const squareIconSx = { borderRadius: 0, border: '0.5px solid', borderColor: BORDER, bgcolor: v('--bg-card'), width: 34, height: 34, color: TEXT, '&:hover': { bgcolor: SKY } } as const;
const storeIconSx = { borderRadius: 0, border: '1px solid', borderColor: v('--border-strong'), bgcolor: SKY_STRONG, color: v('--secondary'), width: 34, height: 34, '&:hover': { bgcolor: SKY, borderColor: v('--secondary') } } as const;

const isRunning = (output: Transcoder) => output.status?.status === 'running' || output.status?.status === 'connected';
const isPullOutput = (output: Transcoder) => output.isPullLink === true || output.deliveryMode === 'PULL';
const SOCIAL_PLATFORM_NAMES = [
  'facebook',
  'facebook live',
  'youtube',
  'youtube live',
  'twitch',
  'instagram',
  'instagram live',
  'tiktok',
  'steam',
  'vimeo',
  'dailymotion',
  'rumble',
  'godtube',
  'bitchute',
  'onlyfans',
  'stripchat',
];
const isSocialStreamOutput = (output: Transcoder) => {
  if (!!(output as any).isSocialStream) return true;
  const platform = String((output as any).platformDistributor || '').trim().toLowerCase();
  return output.protocol === 'rtmp'
    && output.videoCodec === 'copy'
    && (!output.audioCodec || output.audioCodec === 'copy')
    && SOCIAL_PLATFORM_NAMES.some((name) => platform === name || platform.includes(name));
};
const isManagedServiceOutput = (output: Transcoder) => !!(output as any).isManagedService;
const isCopyPushOutput = (output: Transcoder) => output.protocol !== 'hls' && output.videoCodec === 'copy' && !isPullOutput(output) && !isManagedServiceOutput(output);
const isDirectOutput = (output: Transcoder) => isCopyPushOutput(output) && !isSocialStreamOutput(output);
const isManagedOutput = (output: Transcoder) => output.protocol !== 'hls' && output.videoCodec !== 'copy' && !isManagedServiceOutput(output);
const isHlsOutput = (output: Transcoder) => output.protocol === 'hls' && output.isHlsSpecial === true;
const isHlsPlusOutput = (output: Transcoder) => output.protocol === 'hls' && output.isHlsSpecial === false;
const bucket = (label: string, owned: number, used: number): CapacityBucket => ({ label, owned, used, available: Math.max(0, owned - used) });

const statusColor = (status?: string | null) => {
  const value = String(status || '').toLowerCase();
  if (value === 'running' || value === 'connected') return v('--success');
  if (['retrying', 'error', 'failed'].includes(value)) return v('--danger');
  if (['connecting', 'pending', 'idle'].includes(value)) return v('--warning');
  return v('--text-muted');
};

const getChannelSummary = (channel: StreamChannel, outputs: Transcoder[]): ChannelSummary => {
  const socialOwned = Number((channel as any).socialStreamLimit || 0);
  const managedOwned = Number((channel as any).cpuLimit || 0);
  const managedServiceOwned = Number((channel as any).managedServiceLimit || 0) + Number((channel as any).managedServiceUpgradeLimit || 0);
  const directOwned = Math.max(0, Number((channel as any).outputLimit || 0) - managedOwned - Number((channel as any).managedServiceLimit || 0));
  const explicitSocialUsed = outputs.filter(isSocialStreamOutput).length;
  const unassignedCopyPushUsed = outputs.filter((output) => isCopyPushOutput(output) && !isSocialStreamOutput(output)).length;
  const directUsed = Math.min(unassignedCopyPushUsed, directOwned);
  const inferredSocialUsed = socialOwned > explicitSocialUsed ? Math.min(Math.max(0, unassignedCopyPushUsed - directUsed), socialOwned - explicitSocialUsed) : 0;
  const direct = bucket('Direct Pass', directOwned, directUsed);
  const social = bucket('Social', socialOwned, explicitSocialUsed + inferredSocialUsed);
  const managedService = bucket('Managed Service', managedServiceOwned, outputs.filter(isManagedServiceOutput).length);
  const managed = bucket('Managed Output', managedOwned, outputs.filter(isManagedOutput).length);
  const hls = bucket('HLS', Number((channel as any).hlsLimit || 0), outputs.filter(isHlsOutput).length);
  const hlsPlus = bucket('HLS+', Number((channel as any).hlsPlusLimit || 0), outputs.filter(isHlsPlusOutput).length);
  const buckets = [direct, social, managedService, managed, hls, hlsPlus];

  return {
    channel,
    outputs,
    activeOutputs: outputs.filter((output) => output.isActive && isRunning(output)).length,
    issues: outputs.filter((output) => output.isActive && !isRunning(output)).length,
    direct,
    social,
    managedService,
    managed,
    hls,
    hlsPlus,
    totalOwned: buckets.reduce((sum, item) => sum + item.owned, 0),
    totalUsed: buckets.reduce((sum, item) => sum + item.used, 0),
    totalAvailable: buckets.reduce((sum, item) => sum + item.available, 0),
  };
};

const CapacityPill = ({ item }: { item: CapacityBucket }) => {
  const pct = item.owned > 0 ? Math.min(100, (item.used / item.owned) * 100) : 0;
  const full = item.owned > 0 && item.available === 0;
  return (
    <Box sx={{ minWidth: 0, border: '0.5px solid', borderColor: full ? v('--warning') : BORDER, bgcolor: full ? v('--warning-light') : SKY, p: 0.75 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography sx={labelSx} noWrap>{item.label}</Typography>
        <Typography sx={monoSx}>{item.used}/{item.owned}</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 2, borderRadius: 0, mt: 0.7, bgcolor: v('--border-subtle') }} />
      <Typography sx={{ fontSize: '0.65rem', color: MUTED, mt: 0.55 }}>{item.available} free</Typography>
    </Box>
  );
};

const SummaryMetric = ({ label, value, helper, color }: { label: string; value: React.ReactNode; helper?: string; color?: string }) => (
  <Box sx={{ flex: { xs: '1 1 110px', sm: '0 1 auto' }, minWidth: 112, borderLeft: color ? `3px solid ${color}` : '3px solid transparent', pl: 1.25 }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, lineHeight: 1.1, color: TEXT }}>{value}</Typography>
    {helper && <Typography sx={{ fontSize: '0.68rem', color: MUTED }}>{helper}</Typography>}
  </Box>
);

const cleanDestination = (output: Transcoder) => {
  const raw = String(output.destination || '').trim();
  if (!raw || raw.toLowerCase() === String(output.protocol || '').toLowerCase()) return output.protocol === 'hls' ? 'Internal HLS output' : 'External destination';
  if (raw.toLowerCase().includes('local_hls/')) return 'Internal HLS output';
  return raw;
};

const formatAuditDate = (value: string) => formatDateTimeDDMMYY(value);

const formatResourceDate = (value?: string | null) => {
  return formatDateDDMMYY(value, 'billing date');
};

const formatMoney = (currency?: string, amount?: number | null) =>
  `${currency || 'USD'} ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const displayServiceType = (type: string) => {
  switch (type) {
    case 'DIRECT_OUTPUT': return 'Direct Pass';
    case 'SOCIAL_STREAM': return 'Social Stream';
    case 'MANAGED_SERVICE': return 'Managed Service';
    case 'MANAGED_SERVICE_UPGRADE': return 'Managed Service Upgrade';
    case 'MANAGED_OUTPUT': return 'Remapped Output';
    case 'MANAGED_UPGRADE': return 'Remapped Upgrade';
    case 'HLS_STANDARD': return 'HLS';
    case 'HLS_SCTE35':
    case 'HLS_PLUS': return 'HLS+';
    case 'EPG_XMLTV': return 'EPG XMLTV';
    case 'EPG_GRACENOTE': return 'EPG Gracenote';
    case 'PLAYKASTER': return 'PlayKaster';
    case 'OUTPUT_PACKAGE': return 'Output Package';
    default: return type.replace(/_/g, ' ');
  }
};

const serviceCancellationQuantity = (service: BillingServiceLine) => {
  if (service.type === 'SOCIAL_STREAM') return Math.max(1, Number(service.metadata?.outputs || service.quantity || 1));
  return Math.max(1, Number(service.quantity || 1));
};

const serviceCancellationType = (service: BillingServiceLine) => service.type === 'HLS_PLUS' ? 'HLS_SCTE35' : service.type;

const pendingCancellationLabel = (item: ResourceCancellation) => displayServiceType(item.resourceType);

const outputTypeLabel = (output: Transcoder) => {
  if (isManagedServiceOutput(output)) return 'Managed Service';
  if (isSocialStreamOutput(output)) return 'Social Stream';
  if (isHlsPlusOutput(output)) return 'HLS+';
  if (isHlsOutput(output)) return 'HLS';
  if (isManagedOutput(output)) return 'Remapped';
  return (output as any).deliveryMode === 'PULL' || (output as any).isPullLink ? 'Direct Pull' : 'Direct Push';
};

const DrawerTabButton = ({ active, label, icon, onClick }: { active: boolean; label: string; icon: React.ReactNode; onClick: () => void }) => (
  <Button size="small" startIcon={icon} onClick={onClick} variant={active ? 'contained' : 'outlined'} sx={{ borderRadius: 0, textTransform: 'none', fontSize: '0.72rem', minWidth: 0, color: active ? undefined : TEXT, borderColor: BORDER, bgcolor: active ? undefined : v('--bg-card') }}>{label}</Button>
);

export const PortalResources: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [channels, setChannels] = useState<StreamChannel[]>([]);
  const [destinations, setDestinations] = useState<Record<number, Transcoder[]>>({});
  const [billingSummary, setBillingSummary] = useState<BillingAccountSummary | null>(null);
  const [cancellations, setCancellations] = useState<ResourceCancellation[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterKey>('ALL');
  const [viewMode, setViewMode] = useState<ResourceViewMode>('row');
  const [selectedChannel, setSelectedChannel] = useState<StreamChannel | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('history');
  const [renameValue, setRenameValue] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [addonStoreOpen, setAddonStoreOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<AuditLogEntry[]>([]);
  const [cancelService, setCancelService] = useState<BillingServiceLine | null>(null);
  const [cancelReason, setCancelReason] = useState('No longer needed.');
  const [cancelQuantity, setCancelQuantity] = useState(1);
  const [cancelTiming, setCancelTiming] = useState<'scheduled' | 'immediate'>('scheduled');
  const [deleteNowText, setDeleteNowText] = useState('');

  const refreshData = () => {
    setLoading(true);
    Promise.all([getMyChannels(), getBillingAccountSummary(), getResourceCancellations()])
      .then(async ([fetchedChannels, billing, cancellationItems]) => {
        setChannels(fetchedChannels);
        setBillingSummary(billing);
        setCancellations(cancellationItems);
        const destMap: Record<number, Transcoder[]> = {};
        await Promise.all(fetchedChannels.map(async (channel) => {
          try { destMap[channel.id] = await getTranscoders(channel, channel.channelHashId || channel.hashId); }
          catch { destMap[channel.id] = []; }
        }));
        setDestinations(destMap);
      })
      .catch(() => showSnackbar('Failed to load resources.', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refreshData(); }, []);

  const summaries = useMemo(() => channels.map((channel) => getChannelSummary(channel, destinations[channel.id] || [])), [channels, destinations]);
  const filteredSummaries = useMemo(() => summaries.filter((summary) => {
    if (filter === 'ACTIVE') return summary.channel.isActive;
    if (filter === 'INACTIVE') return !summary.channel.isActive;
    if (filter === 'ISSUES') return summary.issues > 0;
    if (filter === 'CAPACITY') return [summary.direct, summary.social, summary.managedService, summary.managed, summary.hls, summary.hlsPlus].some((item) => item.owned > 0 && item.available === 0);
    return true;
  }), [filter, summaries]);
  const totals = useMemo(() => ({
    channels: channels.length,
    activeChannels: channels.filter((channel) => channel.isActive).length,
    owned: summaries.reduce((sum, item) => sum + item.totalOwned, 0),
    used: summaries.reduce((sum, item) => sum + item.totalUsed, 0),
    available: summaries.reduce((sum, item) => sum + item.totalAvailable, 0),
    managedOwned: summaries.reduce((sum, item) => sum + item.managed.owned, 0),
    managedUsed: summaries.reduce((sum, item) => sum + item.managed.used, 0),
    managedAvailable: summaries.reduce((sum, item) => sum + item.managed.available, 0),
    managedServiceOwned: summaries.reduce((sum, item) => sum + item.managedService.owned, 0),
    managedServiceUsed: summaries.reduce((sum, item) => sum + item.managedService.used, 0),
    managedServiceAvailable: summaries.reduce((sum, item) => sum + item.managedService.available, 0),
    issues: summaries.reduce((sum, item) => sum + item.issues, 0),
  }), [channels, summaries]);

  const openDrawer = (channel: StreamChannel, tab: DrawerTab = 'history') => {
    setSelectedChannel(channel);
    setRenameValue(channel.name || '');
    setDrawerTab(tab);
    setDrawerOpen(true);
  };

  useEffect(() => {
    if (!drawerOpen || drawerTab !== 'history' || !selectedChannel?.id) return;
    let cancelled = false;
    setHistoryLoading(true);
    getChannelLogs(selectedChannel)
      .then((data) => {
        if (!cancelled) setHistoryLogs(Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []));
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryLogs([]);
          showSnackbar('Failed to load channel history.', 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [drawerOpen, drawerTab, selectedChannel?.id, showSnackbar]);

  const handleToggleChannel = async (channel: StreamChannel) => {
    try {
      await updateChannel(channel, { isActive: !channel.isActive }, channel.channelHashId || channel.hashId);
      showSnackbar(channel.isActive ? 'Channel and resources disabled.' : 'Channel enabled.', 'success');
      if (selectedChannel?.id === channel.id) setSelectedChannel({ ...selectedChannel, isActive: !channel.isActive });
      refreshData();
    } catch { showSnackbar('Failed to update channel.', 'error'); }
  };

  const handleRenameChannel = async () => {
    if (!selectedChannel || !renameValue.trim()) return;
    setActionLoading(true);
    try {
      await updateChannel(selectedChannel, { name: renameValue.trim() }, selectedChannel.channelHashId || selectedChannel.hashId);
      showSnackbar('Channel renamed.', 'success');
      setSelectedChannel({ ...selectedChannel, name: renameValue.trim() });
      setRenameOpen(false);
      refreshData();
    } catch { showSnackbar('Failed to rename channel.', 'error'); }
    finally { setActionLoading(false); }
  };

  const closeConfirm = () => {
    setConfirmAction(null);
    setConfirmPassword('');
    setConfirmText('');
  };

  const handleConfirmAction = async () => {
    if (!selectedChannel || !confirmAction) return;
    setActionLoading(true);
    try {
      if (confirmAction === 'resetKey') {
        await resetStreamKey(selectedChannel, confirmPassword);
        showSnackbar('Stream key changed. Update your encoder before going live again.', 'success');
      }
      if (confirmAction === 'delete') {
        if (confirmText !== selectedChannel.name) {
          showSnackbar('Type the channel name exactly to delete.', 'warning');
          setActionLoading(false);
          return;
        }
        await deleteChannel(selectedChannel, selectedChannel.channelHashId || selectedChannel.hashId);
        showSnackbar('Channel scheduled for deletion and suspended immediately.', 'success');
        setDrawerOpen(false);
      }
      closeConfirm();
      refreshData();
    } catch (e: any) { showSnackbar(e.response?.data?.message || 'Action failed.', 'error'); }
    finally { setActionLoading(false); }
  };

  const selectedBillingChannel = selectedChannel
    ? billingSummary?.services.channels.find((channel) => Number(channel.id) === Number(selectedChannel.id)) || null
    : null;
  const selectedPaidServices = (selectedBillingChannel?.services || []).filter((service) =>
    CANCELLABLE_SERVICE_TYPES.has(service.type) && Number(service.monthlyAmount || service.unitPrice || 0) > 0
  );
  const pendingCancellationFor = (service: BillingServiceLine) => cancellations.find((item) =>
    Number(item.channelId) === Number(selectedChannel?.id) &&
    String(item.status || '').toUpperCase() === 'PENDING' &&
    item.resourceType === serviceCancellationType(service)
  ) || null;
  const selectedChannelPendingDeletion = selectedChannel ? cancellations.find((item) =>
    Number(item.channelId) === Number(selectedChannel.id) &&
    String(item.status || '').toUpperCase() === 'PENDING' &&
    item.resourceType === 'CHANNEL'
  ) || null : null;
  const selectedPaidServiceTypes = new Set(selectedPaidServices.map((service) => serviceCancellationType(service)));
  const pendingServiceCancellations = selectedChannel ? cancellations.filter((item) =>
    Number(item.channelId) === Number(selectedChannel.id) &&
    String(item.status || '').toUpperCase() === 'PENDING' &&
    item.resourceType !== 'CHANNEL' &&
    !selectedPaidServiceTypes.has(item.resourceType)
  ) : [];

  const openCancelServiceDialog = (service: BillingServiceLine) => {
    const qty = serviceCancellationQuantity(service);
    setCancelService(service);
    setCancelQuantity(qty);
    setCancelReason('No longer needed.');
    setCancelTiming('scheduled');
    setDeleteNowText('');
  };

  const closeCancelServiceDialog = () => {
    setCancelService(null);
    setCancelReason('No longer needed.');
    setCancelQuantity(1);
    setCancelTiming('scheduled');
    setDeleteNowText('');
  };

  const handleScheduleServiceCancellation = async () => {
    if (!selectedChannel || !cancelService) return;
    setActionLoading(true);
    try {
      const channelId = selectedChannel.channelHashId || selectedChannel.hashId || selectedChannel.id;
      const payload = {
        channelId,
        resourceType: serviceCancellationType(cancelService),
        resourceId: cancelService.type === 'OUTPUT_PACKAGE' ? String(cancelService.metadata?.packageCode || '') : undefined,
        quantity: Math.max(1, Number(cancelQuantity || 1)),
        reason: cancelReason.trim() || 'No longer needed.',
      };
      if (cancelTiming === 'immediate') {
        const result = await deleteResourceNow({ ...payload, confirmation: 'DELETE NOW' });
        showSnackbar(result.message || `${displayServiceType(cancelService.type)} deleted immediately.`, 'success');
      } else {
        await scheduleResourceCancellation(payload);
        showSnackbar(`${displayServiceType(cancelService.type)} deletion scheduled.`, 'success');
      }
      closeCancelServiceDialog();
      refreshData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to schedule service deletion.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUndoCancellation = async (id: number) => {
    setActionLoading(true);
    try {
      await undoResourceCancellation(id);
      showSnackbar('Scheduled deletion cancelled.', 'success');
      refreshData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to undo deletion.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const renderResourceRow = (summary: ChannelSummary) => (
    <Paper key={summary.channel.id} elevation={0} sx={{ ...cardSx, overflow: 'hidden', bgcolor: v('--bg-card') }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(170px, 0.55fr) minmax(0, 2.2fr)', xl: 'minmax(180px, 0.55fr) minmax(720px, 2.35fr) minmax(130px, 0.35fr)' }, gap: { xs: 1.25, md: 1.5 }, p: { xs: 1, sm: 1.5 }, alignItems: 'center' }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.65 }}>
            <Chip label={summary.channel.isActive ? 'ACTIVE' : 'PAUSED'} size="small" color={summary.channel.isActive ? 'success' : 'default'} sx={{ borderRadius: 0, height: 22, fontSize: '0.62rem', fontWeight: 800 }} />
            {summary.issues > 0 && <Chip label={`${summary.issues} ISSUE${summary.issues > 1 ? 'S' : ''}`} size="small" color="warning" sx={{ borderRadius: 0, height: 22, fontSize: '0.62rem', fontWeight: 800 }} />}
          </Stack>
          <Typography sx={{ fontSize: { xs: '0.92rem', sm: '1rem' }, fontWeight: 900, color: TEXT }} noWrap>{summary.channel.name}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>{summary.outputs.length} output{summary.outputs.length === 1 ? '' : 's'}</Typography>
          </Stack>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(112px, 1fr))' }, gap: 1, minWidth: 0 }}>
          <CapacityPill item={summary.direct} /><CapacityPill item={summary.social} /><CapacityPill item={summary.managedService} /><CapacityPill item={summary.managed} /><CapacityPill item={summary.hls} /><CapacityPill item={summary.hlsPlus} />
        </Box>
        <Stack direction="row" spacing={0.75} justifyContent={{ xs: 'flex-start', xl: 'flex-end' }} flexWrap="wrap" useFlexGap sx={{ gridColumn: { xs: '1', lg: '1 / -1', xl: 'auto' } }}>
          <Tooltip title="History"><IconButton size="small" onClick={() => openDrawer(summary.channel, 'history')} sx={squareIconSx}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Settings"><IconButton size="small" onClick={() => openDrawer(summary.channel, 'settings')} sx={squareIconSx}><SettingsIcon fontSize="small" /></IconButton></Tooltip>
          <Switch size="small" checked={summary.channel.isActive} onChange={() => handleToggleChannel(summary.channel)} />
        </Stack>
      </Box>
    </Paper>
  );

  const selectedSummary = selectedChannel ? getChannelSummary(selectedChannel, destinations[selectedChannel.id] || []) : null;
  const resourcesBody = viewMode === 'map'
    ? <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>{filteredSummaries.map(renderResourceRow)}</Box>
    : <Stack spacing={1}>{filteredSummaries.map(renderResourceRow)}</Stack>;

  const renderDrawerTab = () => {
    if (!selectedSummary) return null;
    if (drawerTab === 'history') return <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}><Box sx={{ px: 1.25, py: 1, borderBottom: '1px solid', borderColor: BORDER, bgcolor: SKY, display: 'flex', alignItems: 'center', gap: 1 }}><HistoryIcon fontSize="small" /><Typography fontWeight={900} sx={{ color: TEXT }}>History</Typography></Box><TableContainer sx={{ maxHeight: 520 }}><Table size="small" stickyHeader><TableHead><TableRow><TableCell sx={labelSx}>When</TableCell><TableCell sx={labelSx}>Action</TableCell><TableCell sx={labelSx}>Details</TableCell></TableRow></TableHead><TableBody>{historyLoading ? <TableRow><TableCell colSpan={3} align="center" sx={{ py: 5 }}><CircularProgress size={24} /></TableCell></TableRow> : historyLogs.length === 0 ? <TableRow><TableCell colSpan={3} align="center" sx={{ py: 5, color: MUTED }}>No audit history recorded for this channel yet.</TableCell></TableRow> : historyLogs.map((log) => <TableRow key={log.id} hover><TableCell sx={{ ...monoSx, fontSize: '0.7rem', verticalAlign: 'top', color: v('--secondary') }}>{formatAuditDate(log.createdAt)}</TableCell><TableCell sx={{ ...monoSx, fontSize: '0.7rem', verticalAlign: 'top' }}>{log.action}</TableCell><TableCell sx={{ fontSize: '0.76rem', color: TEXT, verticalAlign: 'top' }}>{formatAuditDetails(log.details)}</TableCell></TableRow>)}</TableBody></Table></TableContainer></Paper>;
    if (drawerTab === 'settings') return (
      <Stack spacing={1.5}>
        <Paper elevation={0} sx={{ ...cardSx, p: 2, bgcolor: SKY }}>
          <Stack spacing={1.5}>
            <Typography fontWeight={900} sx={{ color: TEXT }}>Settings</Typography>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Box>
                <Typography fontWeight={700} sx={{ color: TEXT }}>Channel state</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.85rem' }}>Disable stops the channel and its resources.</Typography>
              </Box>
              <Switch size="small" checked={selectedSummary.channel.isActive} onChange={() => handleToggleChannel(selectedSummary.channel)} />
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ ...cardSx, p: 2, bgcolor: v('--bg-card') }}>
          <Stack spacing={1.25}>
            <Typography fontWeight={900} sx={{ color: TEXT }}>Channel management</Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Rename channel"><IconButton color="primary" onClick={() => setRenameOpen(true)} sx={squareIconSx}><EditIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Reset stream key"><IconButton color="warning" onClick={() => setConfirmAction('resetKey')} sx={squareIconSx}><KeyIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title={selectedChannelPendingDeletion ? 'Channel deletion already scheduled' : 'Delete channel'}><span><IconButton color="error" disabled={!!selectedChannelPendingDeletion} onClick={() => setConfirmAction('delete')} sx={squareIconSx}><DeleteForeverIcon fontSize="small" /></IconButton></span></Tooltip>
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ ...cardSx, p: 2, bgcolor: v('--bg-card') }}>
          <Stack spacing={1.25}>
            <Box>
              <Typography fontWeight={900} sx={{ color: TEXT }}>Purchased services</Typography>
              <Typography sx={{ color: MUTED, fontSize: '0.78rem', mt: 0.25 }}>Schedule paid services for removal at the billing boundary.</Typography>
            </Box>
            {selectedPaidServices.length === 0 && !selectedChannelPendingDeletion && pendingServiceCancellations.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 0 }}>No removable paid services are attached to this channel.</Alert>
            ) : (
              <Stack spacing={0.85}>
                {selectedChannelPendingDeletion && (
                  <Box sx={{ border: '0.5px solid', borderColor: BORDER, bgcolor: SKY, p: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: TEXT }} noWrap>Channel base service</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: MUTED }} noWrap>{selectedChannel?.name || 'Channel'}</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: v('--warning'), mt: 0.35 }}>Deletion scheduled for {formatResourceDate(selectedChannelPendingDeletion.effectiveAt)}</Typography>
                      </Box>
                      <Button size="small" variant="outlined" disabled={actionLoading} onClick={() => handleUndoCancellation(selectedChannelPendingDeletion.id)} startIcon={<RestoreIcon fontSize="small" />} sx={{ borderRadius: 0, textTransform: 'none' }}>Undo</Button>
                    </Stack>
                  </Box>
                )}
                {pendingServiceCancellations.map((item) => (
                  <Box key={`pending-${item.id}`} sx={{ border: '0.5px solid', borderColor: BORDER, bgcolor: SKY, p: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: TEXT }} noWrap>{pendingCancellationLabel(item)}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: MUTED }} noWrap>{Number(item.quantity || 1)} scheduled for removal</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: v('--warning'), mt: 0.35 }}>Deletion scheduled for {formatResourceDate(item.effectiveAt)}</Typography>
                      </Box>
                      <Button size="small" variant="outlined" disabled={actionLoading} onClick={() => handleUndoCancellation(item.id)} startIcon={<RestoreIcon fontSize="small" />} sx={{ borderRadius: 0, textTransform: 'none' }}>Undo</Button>
                    </Stack>
                  </Box>
                ))}
                {selectedPaidServices.map((service) => {
                  const pending = pendingCancellationFor(service);
                  return (
                    <Box key={`${service.type}-${service.name}`} sx={{ border: '0.5px solid', borderColor: BORDER, bgcolor: SKY, p: 1 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: TEXT }} noWrap>{displayServiceType(service.type)}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: MUTED }} noWrap>{service.description || service.name}</Typography>
                          <Typography sx={{ ...monoSx, fontSize: '0.68rem', color: v('--secondary') }}>{formatMoney(billingSummary?.account.currency, service.monthlyAmount)}/mo</Typography>
                          {pending && <Typography sx={{ fontSize: '0.7rem', color: v('--warning'), mt: 0.35 }}>Deletion scheduled for {formatResourceDate(pending.effectiveAt)}</Typography>}
                        </Box>
                        {pending ? (
                          <Button size="small" variant="outlined" disabled={actionLoading} onClick={() => handleUndoCancellation(pending.id)} sx={{ borderRadius: 0, textTransform: 'none' }}>Undo</Button>
                        ) : (
                          <Button size="small" color="error" variant="outlined" disabled={actionLoading} onClick={() => openCancelServiceDialog(service)} sx={{ borderRadius: 0, textTransform: 'none' }}>Delete</Button>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>
    );
    return null;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', bgcolor: LIGHT, color: TEXT }}>
      <ConsolePageShell
        title="Resources"
        eyebrow="Channel Capacity"
        subtitle="Channels, output capacity, and service allocation."
        actions={(
          <>
            <ResourceViewControls mode={viewMode} onModeChange={setViewMode} onRefresh={refreshData} />
            <Tooltip title="Add Channel"><IconButton onClick={() => setAddonStoreOpen(true)} sx={storeIconSx}><AddCircleOutlineIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Store"><IconButton onClick={() => setAddonStoreOpen(true)} sx={storeIconSx}><StorefrontIcon fontSize="small" /></IconButton></Tooltip>
          </>
        )}
        stats={[
          { label: 'Channels', value: `${totals.activeChannels}/${totals.channels}`, helper: 'active / total', tone: 'success' },
          { label: 'Resources', value: `${totals.used}/${totals.owned}`, helper: `${totals.available} free` },
          { label: 'Remapped', value: `${totals.managedUsed}/${totals.managedOwned}`, helper: `${totals.managedAvailable} free` },
          { label: 'Issues', value: totals.issues, helper: 'active output warnings', tone: totals.issues > 0 ? 'warning' : 'success' },
        ]}
      >
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>{(['ALL', 'ACTIVE', 'INACTIVE', 'ISSUES', 'CAPACITY'] as FilterKey[]).map((key) => <Chip key={key} label={key.replace('_', ' ')} onClick={() => setFilter(key)} color={filter === key ? 'primary' : 'default'} variant={filter === key ? 'filled' : 'outlined'} sx={{ borderRadius: 0, fontWeight: 700, bgcolor: filter === key ? undefined : v('--bg-card'), color: filter === key ? undefined : TEXT, borderColor: BORDER }} />)}</Stack>
      {loading ? <Box textAlign="center" py={6}><CircularProgress /></Box> : filteredSummaries.length === 0 ? <Alert severity="info" sx={{ borderRadius: 0, bgcolor: SKY, color: TEXT }}>No resources match this filter.</Alert> : resourcesBody}
      </ConsolePageShell>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 620, md: 760 }, maxWidth: '100vw', bgcolor: LIGHT, color: TEXT } }}>
        <Box sx={{ p: { xs: 1, sm: 1.5 }, borderBottom: '1px solid', borderColor: BORDER, bgcolor: SKY, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}><Box sx={{ minWidth: 0 }}><Typography variant="h6" fontWeight={900} noWrap sx={{ color: TEXT }}>{selectedChannel?.name || 'Channel'}</Typography></Box><IconButton onClick={() => setDrawerOpen(false)} sx={squareIconSx}><CloseIcon /></IconButton></Box>
        <Box sx={{ px: { xs: 1, sm: 1.5 }, pt: 1.5 }}><Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap><DrawerTabButton active={drawerTab === 'history'} label="History" icon={<HistoryIcon fontSize="small" />} onClick={() => setDrawerTab('history')} /><DrawerTabButton active={drawerTab === 'settings'} label="Settings" icon={<SettingsIcon fontSize="small" />} onClick={() => setDrawerTab('settings')} /></Stack></Box>
        <Box sx={{ p: { xs: 1, sm: 1.5 } }}>{renderDrawerTab()}</Box>
      </Drawer>

      <Dialog open={addonStoreOpen} onClose={() => setAddonStoreOpen(false)} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 0, bgcolor: LIGHT, color: TEXT, maxHeight: '92vh' } }}>
        <DialogTitle sx={{ fontWeight: 900, bgcolor: SKY, borderBottom: '1px solid', borderColor: BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}><StorefrontIcon fontSize="small" /><Typography fontWeight={900}>Addon Store</Typography></Stack>
          <IconButton onClick={() => setAddonStoreOpen(false)} sx={squareIconSx}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1, sm: 2 } }}><AddonStore /></DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0, bgcolor: v('--bg-card'), color: TEXT } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Rename Channel</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Channel name" value={renameValue} onChange={(event) => setRenameValue(event.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} sx={{ borderRadius: 0 }}>Cancel</Button>
          <Button variant="contained" onClick={handleRenameChannel} disabled={actionLoading || !renameValue.trim() || renameValue.trim() === selectedChannel?.name} sx={{ borderRadius: 0 }}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmAction} onClose={closeConfirm} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0, bgcolor: v('--bg-card'), color: TEXT } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>{confirmAction === 'delete' ? 'Delete Channel' : 'Reset Stream Key'}</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>{confirmAction === 'delete' ? <><Alert severity="error" sx={{ borderRadius: 0 }}>This permanently deletes the channel. Type the channel name to confirm.</Alert><TextField size="small" label="Channel name" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} /></> : <><Alert severity="warning" sx={{ borderRadius: 0 }}>This changes the ingest stream key. Current encoders must be updated.</Alert><TextField size="small" type="password" label="Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} /></>}</Stack></DialogContent>
        <DialogActions><Button onClick={closeConfirm} sx={{ borderRadius: 0 }}>Cancel</Button><Button variant="contained" color={confirmAction === 'delete' ? 'error' : 'warning'} disabled={actionLoading || (confirmAction === 'delete' && confirmText !== selectedChannel?.name) || (confirmAction === 'resetKey' && !confirmPassword)} onClick={handleConfirmAction} sx={{ borderRadius: 0 }}>Confirm</Button></DialogActions>
      </Dialog>

      <Dialog open={!!cancelService} onClose={closeCancelServiceDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0, bgcolor: v('--bg-card'), color: TEXT } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Delete {cancelService ? displayServiceType(cancelService.type) : 'Service'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Alert severity="warning" sx={{ borderRadius: 0 }}>
              {cancelTiming === 'scheduled'
                ? 'This schedules the service for removal at the next billing boundary. It does not refund or change the original billing date.'
                : 'This removes the service immediately. It does not refund, credit, or reverse a paid invoice. Only future recurring charges are recalculated.'}
            </Alert>
            <RadioGroup row value={cancelTiming} onChange={(event) => { setCancelTiming(event.target.value as 'scheduled' | 'immediate'); setDeleteNowText(''); }}>
              <FormControlLabel value="scheduled" control={<Radio size="small" />} label="At billing date" />
              <FormControlLabel value="immediate" control={<Radio size="small" />} label="Delete now" />
            </RadioGroup>
            <TextField
              size="small"
              type="number"
              label="Quantity"
              value={cancelQuantity}
              onChange={(event) => setCancelQuantity(Math.max(1, Number(event.target.value || 1)))}
              inputProps={{ min: 1, max: cancelService ? serviceCancellationQuantity(cancelService) : 1 }}
              fullWidth
            />
            {cancelTiming === 'immediate' && (
              <TextField
                size="small"
                label="Type DELETE NOW to confirm"
                value={deleteNowText}
                onChange={(event) => setDeleteNowText(event.target.value)}
                fullWidth
                error={deleteNowText.length > 0 && deleteNowText !== 'DELETE NOW'}
              />
            )}
            <TextField
              size="small"
              label="Reason"
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelServiceDialog} disabled={actionLoading} sx={{ borderRadius: 0 }}>Cancel</Button>
          <Button color="error" variant="contained" disabled={actionLoading || !cancelReason.trim() || (cancelTiming === 'immediate' && deleteNowText !== 'DELETE NOW')} onClick={handleScheduleServiceCancellation} sx={{ borderRadius: 0 }}>
            {cancelTiming === 'immediate' ? 'Delete now' : 'Schedule deletion'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
